import React, { useState, useEffect, useCallback } from "react";
import { Modal, Toast, ToastContainer } from "react-bootstrap";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import { unwrapArrayPayload } from "../../utils/teacherApi";
import AppSpinner from "../Spinner/AppSpinner";

const styles = `
  .cfpm-body { padding: 20px; background: #fff; }

  .cfpm-section {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .cfpm-section:last-child { margin-bottom: 0; }

  .cfpm-section-title {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    color: #9ca3af; letter-spacing: 0.05em; margin-bottom: 12px;
    display: flex; align-items: center; gap: 6px;
  }

  .cfpm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .cfpm-row-full { display: grid; grid-template-columns: 1fr; gap: 12px; }

  .cfpm-field { display: flex; flex-direction: column; gap: 4px; }

  .cfpm-label {
    font-size: 11px; font-weight: 700; color: #374151;
    text-transform: uppercase; letter-spacing: 0.03em;
  }

  .cfpm-select, .cfpm-input {
    width: 100%; padding: 8px 10px;
    border: 1px solid #d1d5db; border-radius: 6px;
    font-size: 13px; color: #374151; background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
    outline: none;
  }
  .cfpm-select:focus, .cfpm-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }
  .cfpm-input-green {
    border-color: #22c55e !important;
    box-shadow: 0 0 0 3px rgba(34,197,94,0.1) !important;
  }

  .cfpm-hint { font-size: 11px; color: #9ca3af; margin-top: 2px; }
  .cfpm-error { font-size: 11px; color: #dc2626; margin-top: 2px; }
  .cfpm-hint-green { font-size: 11px; color: #16a34a; margin-top: 2px; font-weight: 600; }

  .cfpm-deadline-preview {
    display: flex; align-items: center; gap: 8px;
    background: #eff6ff; border: 1px solid #bfdbfe;
    border-radius: 6px; padding: 8px 12px;
    font-size: 12px; color: #1d4ed8; font-weight: 600;
    margin-top: 4px;
  }

  .cfpm-btn-cancel {
    padding: 7px 18px; border-radius: 6px; border: 1px solid #d1d5db;
    background: #fff; color: #374151; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: background 0.15s;
  }
  .cfpm-btn-cancel:hover { background: #f3f4f6; }
  .cfpm-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

  .cfpm-btn-save {
    padding: 7px 22px; border-radius: 6px; border: none;
    background: #22c55e; color: #fff; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: background 0.15s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .cfpm-btn-save:hover { background: #16a34a; }
  .cfpm-btn-save:disabled { background: #86efac; cursor: not-allowed; }
`;

const projectLabel = (p) => p.name ?? p.title ?? p.projectName ?? `Layihə #${p.id ?? p.contractTrainingProjectId ?? ""}`;
const projectValue = (p) => String(p.id ?? p.contractTrainingProjectId ?? "");

const projectDayLimit = (p) => Number(p?.dayLimit ?? p?.durationDays ?? 0);

const calcDeadline = (beginDate, dayLimit) => {
  if (!beginDate || !dayLimit || Number(dayLimit) < 1) return null;
  const d = new Date(beginDate);
  d.setDate(d.getDate() + Number(dayLimit));
  return d.toLocaleDateString("az-AZ", { day: "2-digit", month: "long", year: "numeric" });
};

const CreateFinalProjectModal = ({ show, onHide, onSuccess, editData }) => {
  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects]     = useState([]);
  const [groups, setGroups]         = useState([]);

  const [contractTrainingProjectId, setContractTrainingProjectId] = useState("");
  const [groupId, setGroupId]           = useState("");
  const [beginDate, setBeginDate] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [toast, setToast] = useState({ show: false, msg: "", variant: "danger" });
  const fireToast = (msg, variant = "danger") => setToast({ show: true, msg, variant });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, projectsRes] = await Promise.all([
        apiFetch(`${BASE_URL}/teacher-groups/status-filter-active-or-unprinted-certificate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupStatus: "Aktiv" }),
        }),
        apiFetch(`${BASE_URL}/contract-training-projects`),
      ]);
      if (groupsRes.ok) {
        const gj = await groupsRes.json();
        setGroups(Array.isArray(gj?.teacherGroups) ? gj.teacherGroups : unwrapArrayPayload(gj, ["teacherGroups", "items", "content"]));
      }
      if (projectsRes.ok) {
        const pj = await projectsRes.json();
        setProjects(unwrapArrayPayload(pj, ["contractTrainingProjects", "items", "content"]));
      }
    } catch (e) {
      console.error(e);
      fireToast("Məlumat yüklənərkən xəta baş verdi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setContractTrainingProjectId(String(editData?.contractTrainingProjectId ?? ""));
    setGroupId(String(editData?.groupId ?? ""));
    setBeginDate(editData?.beginDate ?? "");
    setFieldErrors({});
    loadData();
  }, [show, editData, loadData]);

  const handleProjectChange = (val) => setContractTrainingProjectId(val);

  const parseFieldErrors = async (res) => {
    try {
      const data = await res.json();
      const validation = data?.validations ?? data?.errors ?? data?.fieldErrors ?? {};
      if (Array.isArray(validation)) {
        return validation.reduce((acc, item) => {
          if (item?.field && item?.message) acc[item.field] = item.message;
          return acc;
        }, {});
      }
      return typeof validation === "object" && validation ? validation : {};
    } catch {
      return {};
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contractTrainingProjectId || !groupId || !beginDate) {
      fireToast("Bütün sahələri doldurun");
      return;
    }
    setSubmitting(true);
    setFieldErrors({});
    try {
      const res = await apiFetch(`${BASE_URL}/teacher-group-projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractTrainingProjectId: Number(contractTrainingProjectId),
          groupId: Number(groupId),
          beginDate,
        }),
      });
      if (!res.ok) {
        const errs = await parseFieldErrors(res);
        setFieldErrors(errs);
        if (!Object.keys(errs).length) fireToast("Yadda saxlama zamanı xəta baş verdi");
        return;
      }
      fireToast("Layihə qrupa təyin edildi", "success");
      onSuccess?.();
      onHide();
    } catch (err) {
      console.error(err);
      fireToast(err.message || "Xəta baş verdi");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedProject = projects.find((p) => projectValue(p) === contractTrainingProjectId);
  const dayLimit = projectDayLimit(selectedProject);
  const deadlinePreview = calcDeadline(beginDate, dayLimit);

  return (
    <>
      <style>{styles}</style>

      <Modal show={show} onHide={onHide} centered size="md" backdrop="static">
        <Modal.Header closeButton style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb", padding: "12px 20px" }}>
          <Modal.Title style={{ fontWeight: 700, color: "#374151", fontSize: "15px" }}>
            <i className="bi bi-folder-plus me-2" style={{ color: "#22c55e" }} />
            Qrupa layihə vermək
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="cfpm-body">
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <AppSpinner inline />
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, marginBottom: 0 }}>Yüklənir...</p>
            </div>
          ) : (
            <form id="cfpm-form" onSubmit={handleSubmit}>

              {/* Layihə + Qrup */}
              <div className="cfpm-section">
                <div className="cfpm-section-title">
                  <i className="bi bi-collection" /> Layihə məlumatları
                </div>
                <div className="cfpm-row">
                  <div className="cfpm-field">
                    <label className="cfpm-label">Layihə</label>
                    <select
                      className="cfpm-select"
                      value={contractTrainingProjectId}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      required
                    >
                      <option value="">Seçin...</option>
                      {projects.map((p) => (
                        <option key={projectValue(p)} value={projectValue(p)}>{projectLabel(p)}</option>
                      ))}
                    </select>
                    {fieldErrors.contractTrainingProjectId && <span className="cfpm-error">{fieldErrors.contractTrainingProjectId}</span>}
                    {projects.length === 0 && <span className="cfpm-hint">Layihə tapılmadı.</span>}
                  </div>

                  <div className="cfpm-field">
                    <label className="cfpm-label">Qrup</label>
                    <select
                      className="cfpm-select"
                      value={groupId}
                      onChange={(e) => setGroupId(e.target.value)}
                      required
                    >
                      <option value="">Seçin...</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name ?? `Qrup #${g.id}`}</option>
                      ))}
                    </select>
                    {fieldErrors.groupId && <span className="cfpm-error">{fieldErrors.groupId}</span>}
                    {groups.length === 0 && <span className="cfpm-hint">Aktiv qrup tapılmadı.</span>}
                  </div>
                </div>
              </div>

              {/* Tarix + Müddət */}
              <div className="cfpm-section">
                <div className="cfpm-section-title">
                  <i className="bi bi-calendar3" /> Tarix
                </div>
                <div className="cfpm-row">
                  <div className="cfpm-field">
                    <label className="cfpm-label">Verilmə tarixi</label>
                    <input
                      type="date"
                      className="cfpm-input"
                      value={beginDate}
                      onChange={(e) => setBeginDate(e.target.value)}
                      required
                    />
                    {fieldErrors.beginDate && <span className="cfpm-error">{fieldErrors.beginDate}</span>}
                  </div>

                  <div className="cfpm-field">
                    <label className="cfpm-label">Layihə günü</label>
                    <input type="text" className="cfpm-input cfpm-input-green" value={dayLimit ? `${dayLimit} gün` : "—"} disabled />
                    <span className="cfpm-hint">Müddət layihədən avtomatik götürülür</span>
                  </div>
                </div>

                {deadlinePreview && (
                  <div className="cfpm-deadline-preview" style={{ marginTop: 12 }}>
                    <i className="bi bi-alarm" />
                    Son təhvil tarixi: <strong>{deadlinePreview}</strong>
                  </div>
                )}
              </div>

            </form>
          )}
        </Modal.Body>

        <Modal.Footer style={{ background: "#f3f4f6", borderTop: "1px solid #e5e7eb", padding: "10px 16px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="cfpm-btn-cancel" onClick={onHide} disabled={submitting}>
            Ləğv et
          </button>
          <button type="submit" form="cfpm-form" className="cfpm-btn-save" disabled={submitting || loading}>
            {submitting
              ? <><AppSpinner inline /> Saxlanılır...</>
              : <><i className="bi bi-check-lg" />Yadda saxla</>
            }
          </button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 100000 }}>
        <Toast bg={toast.variant} show={toast.show} onClose={() => setToast(t => ({ ...t, show: false }))} delay={2500} autohide>
          <Toast.Header closeButton>
            <strong className="me-auto">{toast.variant === "success" ? "Uğurlu" : "Xəta"}</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default CreateFinalProjectModal;
